import {
  AdminCreateOrganizationDto,
  CompanyCreateDto,
  CompanyRevenueDtoPeriodEnum,
  CompanySelectDto,
  CompanyType,
  ResearchOrganizationFundingType,
} from '@@types/api.types.ts';
import { Modal } from '@components/core/modal.component.tsx';
import { AdminIntelTeamStep } from '@components/pages/company/components/company-company-profile-modal-admin-intel-team-step.component.tsx';
import { DetailsIntelSection } from '@components/pages/company/components/company-company-profile-modal-details-intel-section.component.tsx';
import { DetailsStep } from '@components/pages/company/components/company-company-profile-modal-details-step.component.tsx';
import { FinancialStep } from '@components/pages/company/components/company-company-profile-modal-financial-step.component.tsx';
import { ProfileStep } from '@components/pages/company/components/company-company-profile-modal-profile-step.component.tsx';
import { SuccessStep } from '@components/pages/company/components/company-company-profile-modal-success-step.component.tsx';
import { TeamStepComponent } from '@components/pages/company/components/company-company-profile-modal-team-step.component.tsx';
import { TypeStep } from '@components/pages/company/components/company-company-profile-modal-type-step.component.tsx';
import { StepConfig, StepState, useSteps } from '@components/shared/modal-steps.component.tsx';
import { useAppForm } from '@components/ui/form/form';
import { errorToast } from '@hooks/toast.hook.tsx';
import { useCreateCompanyProfile, useUpdateCompany } from '@queries/company.query.ts';
import { useAdminCreateOrganization } from '@queries/intel-organization.query.ts';
import {
  CompanyFormData,
  companyFormSchema,
  companyFormSchemaBase,
  detailsCommercialSchema,
  detailsIntelSchema,
  detailsResearchSchema,
  financialStepSchema,
  profileStepSchema,
  typeStepSchema,
} from '@schemas/company.schema';
import { useStore } from '@tanstack/react-form';
import { isResearchType } from '@utils/company.util';
import { countries, usStates } from '@utils/constants';
import { removeEmptyStrings } from '@utils/form-validator.util';
import { createFormInvalidHandler, createStepValidationError, resetFieldMeta } from '@utils/form.util';
import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod/v4';
import { useCompanyProfileModalInvites } from './company-company-profile-modal-invites.hook';
import { useCompanyProfileModalLogo } from './company-company-profile-modal-logo.hook';
import {
  PrepareCompanySubmissionResult,
  useCompanyProfileModalSubmit,
} from './company-company-profile-modal-submit.hook';
import { CompanyEditNavBar } from './company-edit-nav-bar.component';
import { companyFormOptions } from './company-form-options';

// AZ-451 D9: 'admin-intel' forks this innovator wizard for Platform-Admin Intelligence-org creation.
// The innovator path stays the default; admin behavior is isolated behind this discriminator.
export type CompanyProfileMode = 'admin-intel' | 'innovator';

type CompanyProfileProps = {
  company?: CompanySelectDto;
  isEdit?: boolean;
  isOpen: boolean;
  mode?: CompanyProfileMode;
  onClose: () => void;
  onCompanyCreated?: (id: string) => void;
  onContinue?: (submit?: boolean) => void;
  width?: string;
};

type CompanyProfileDetailsPresentation = {
  showCompanyLogo: boolean;
};

export enum Steps {
  TYPE = 0,
  DETAILS = 1,
  FINANCIAL = 2,
  PROFILE = 3,
  TEAM = 4,
}

const getStepSchema = (step: Steps, type: CompanyType | undefined, mode: CompanyProfileMode) => {
  if (mode === 'admin-intel') {
    // Admin-intel steps are Type -> Details -> Team (no Financial, no Profile). TYPE is presentational
    // (type forced to INTELLIGENCE at submit) and TEAM is managed live, so only DETAILS is validated.
    return step === Steps.DETAILS ? detailsIntelSchema : undefined;
  }

  switch (step) {
    case Steps.TYPE:
      return typeStepSchema;
    case Steps.DETAILS:
      return isResearchType(type) ? detailsResearchSchema : detailsCommercialSchema;
    case Steps.FINANCIAL:
      return financialStepSchema;
    case Steps.PROFILE:
      return profileStepSchema;
    default:
      return undefined;
  }
};

export const getCompanyProfileDetailsPresentation = (params: {
  companyType: CompanyType | undefined;
  isEdit: boolean;
  mode: CompanyProfileMode;
}): CompanyProfileDetailsPresentation => {
  const { companyType, isEdit, mode } = params;

  return { showCompanyLogo: mode !== 'admin-intel' || (isEdit && companyType === CompanyType.INTELLIGENCE) };
};

export const buildCompanyProfileStepData = (params: {
  companyType: CompanyType | undefined;
  isEdit: boolean;
  mode: CompanyProfileMode;
}): StepConfig[] => {
  const { companyType, isEdit, mode } = params;
  const isAdminIntel = mode === 'admin-intel';
  const isIntelEdit = isEdit && companyType === CompanyType.INTELLIGENCE;

  const baseSteps: StepConfig[] = isIntelEdit
    ? []
    : [
        {
          guidance: isAdminIntel
            ? 'Only Intelligence organizations can be created from the admin console.'
            : 'Choose the type of organization.',
          label: 'Type',
          step: Steps.TYPE,
        },
      ];

  baseSteps.push({
    guidance: '',
    label: 'Details',
    step: Steps.DETAILS,
  });

  // Admin-intel orgs skip Financial (innovator/Commercial-only) and Profile — just Type -> Details -> Team.
  if (!isAdminIntel) {
    if (companyType === CompanyType.COMMERCIAL) {
      baseSteps.push({ guidance: '', label: 'Financial', step: Steps.FINANCIAL });
    }
    baseSteps.push({ guidance: '', label: 'Profile', step: Steps.PROFILE });
  }

  baseSteps.push({ guidance: '', label: 'Team', step: Steps.TEAM });

  return baseSteps;
};

// AZ-451 D9: builds the atomic admin-create payload from the wizard form values, forcing
// type=INTELLIGENCE and normalizing country/state the same way the innovator submission does.
// The admin flow has no Profile step, so URLs are not collected.
const buildAdminIntelPayload = (value: CompanyFormData): AdminCreateOrganizationDto | undefined => {
  const parsed = detailsIntelSchema.safeParse(value);

  if (!parsed.success) {
    return undefined;
  }

  const data = parsed.data;

  const normalizedCountry =
    countries.find((country) => country.name === data.country)?.code || data.country || undefined;

  const normalizedState =
    data.state && data.country === 'US'
      ? usStates.find((state) => state.code === data.state || state.name === data.state)?.code || data.state
      : data.state;

  const payload: AdminCreateOrganizationDto = {
    city: data.city,
    country: normalizedCountry,
    description: data.description,
    name: data.name,
    state: normalizedState,
    type: CompanyType.INTELLIGENCE,
  };

  return removeEmptyStrings(payload, true);
};

const createBaseDefaults = (): CompanyFormData => {
  const defaults = companyFormOptions.defaultValues;

  const baseDefaults = {
    ...defaults,
    researchOrganizationData: defaults.researchOrganizationData
      ? {
          ...defaults.researchOrganizationData,
          departmentHeadContact: {
            email: defaults.researchOrganizationData.departmentHeadContact?.email || '',
            name: defaults.researchOrganizationData.departmentHeadContact?.name || '',
          },
          techTransferContact: {
            email: defaults.researchOrganizationData.techTransferContact?.email || '',
            name: defaults.researchOrganizationData.techTransferContact?.name || '',
          },
          foreignAffiliates: [...(defaults.researchOrganizationData.foreignAffiliates || [])],
          funding: {
            countries: [...(defaults.researchOrganizationData.funding?.countries || [])],
            type: defaults.researchOrganizationData.funding?.type || undefined,
          },
        }
      : undefined,
    revenue: {
      amount: defaults.revenue?.amount ?? undefined,
      period: defaults.revenue?.period ?? CompanyRevenueDtoPeriodEnum.PerYear,
      valuation: defaults.revenue?.valuation ?? undefined,
    },
    urls: {
      crunchbase: defaults.urls?.crunchbase || '',
      linkedin: defaults.urls?.linkedin || '',
      twitter: defaults.urls?.twitter || '',
      website: defaults.urls?.website || '',
    },
  } as CompanyFormData;

  const { hasForeignAffiliation, hasFunding } = deriveResearchFlags(baseDefaults);

  return {
    ...baseDefaults,
    hasForeignAffiliation,
    hasFunding,
  };
};

const mapCompanyToFormValues = (defaults: CompanyFormData, company: CompanySelectDto): CompanyFormData => {
  const baseValues = {
    ...defaults,
    city: company.city || '',
    cmmcLevel: company.cmmcLevel || undefined,
    country: company.country || defaults.country,
    description: company.description || '',
    name: company.name || '',
    previousGovernmentWorkCountries: company.previousGovernmentWorkCountries?.length
      ? [...company.previousGovernmentWorkCountries]
      : [],
    previousGovernmentWorkServiceIds: company.previousGovernmentWorkServices?.length
      ? company.previousGovernmentWorkServices.map((s) => s.id)
      : [],
    researchOrganizationData: company.researchOrganizationData
      ? {
          departmentHeadContact: {
            email: company.researchOrganizationData.departmentHeadContact?.email || '',
            name: company.researchOrganizationData.departmentHeadContact?.name || '',
          },
          departmentName: company.researchOrganizationData.departmentName || '',
          foreignAffiliates: company.researchOrganizationData.foreignAffiliates?.length
            ? [...company.researchOrganizationData.foreignAffiliates]
            : [],
          funding: {
            countries: company.researchOrganizationData.funding?.countries?.length
              ? [...company.researchOrganizationData.funding.countries]
              : [],
            type: company.researchOrganizationData.funding?.type || undefined,
          },
          ipLicense:
            company.researchOrganizationData.ipLicense === undefined
              ? undefined
              : company.researchOrganizationData.ipLicense,
          techTransferContact: {
            email: company.researchOrganizationData.techTransferContact?.email || '',
            name: company.researchOrganizationData.techTransferContact?.name || '',
          },
          type: company.researchOrganizationData.type || undefined,
        }
      : defaults.researchOrganizationData,
    revenue: {
      amount: company.revenue?.amount ?? undefined,
      period: company.revenue?.period || CompanyRevenueDtoPeriodEnum.PerYear,
      valuation: company.revenue?.valuation ?? undefined,
    },
    samsUeiNumber: company.samsUeiNumber || '',
    state: company.state || '',
    type: company.type || CompanyType.COMMERCIAL,
    urls: {
      crunchbase: company.urls?.crunchbase || '',
      linkedin: company.urls?.linkedin || '',
      twitter: company.urls?.twitter || '',
      website: company.urls?.website || '',
    },
    yearFounded: company.yearFounded || undefined,
  } as CompanyFormData;

  const { hasForeignAffiliation, hasFunding } = deriveResearchFlags(baseValues);

  return {
    ...baseValues,
    hasForeignAffiliation,
    hasFunding,
  };
};

const deriveResearchFlags = (value: CompanyFormData) => {
  const foreignAffiliates = value.researchOrganizationData?.foreignAffiliates;
  const funding = value.researchOrganizationData?.funding;

  return {
    hasForeignAffiliation: Boolean(foreignAffiliates && foreignAffiliates.length > 0),
    hasFunding: Boolean(funding && funding.type !== undefined),
  };
};

const intelCompanyFormSchema = companyFormSchemaBase.extend({
  type: z.literal(CompanyType.INTELLIGENCE),
});

export const prepareSubmission = (value: CompanyFormData): PrepareCompanySubmissionResult => {
  const sanitizedValue: CompanyFormData = {
    ...value,
    researchOrganizationData: value.researchOrganizationData
      ? {
          ...value.researchOrganizationData,
          funding: value.researchOrganizationData.funding ? { ...value.researchOrganizationData.funding } : undefined,
        }
      : undefined,
  };

  const funding = sanitizedValue.researchOrganizationData?.funding;
  if (funding && funding.type !== ResearchOrganizationFundingType.FOREIGN_NATION) {
    sanitizedValue.researchOrganizationData!.funding = {
      ...funding,
      countries: undefined,
    };
  }

  const schema = sanitizedValue.type === CompanyType.INTELLIGENCE ? intelCompanyFormSchema : companyFormSchema;
  const parsed = schema.safeParse(sanitizedValue);

  if (!parsed.success) {
    return { success: false };
  }

  const { hasForeignAffiliation, hasFunding, ...companyData } = parsed.data;

  const normalizedCountry =
    countries.find((country) => country.name === companyData.country)?.code || companyData.country || undefined;

  const normalizedState =
    companyData.state && companyData.country === 'US'
      ? usStates.find((state) => state.code === companyData.state || state.name === companyData.state)?.code ||
        companyData.state
      : companyData.state;

  const normalizedResearchOrganizationData = companyData.researchOrganizationData
    ? { ...companyData.researchOrganizationData }
    : undefined;

  if (normalizedResearchOrganizationData) {
    if (!hasForeignAffiliation) {
      normalizedResearchOrganizationData.foreignAffiliates = undefined;
    }

    if (!hasFunding) {
      normalizedResearchOrganizationData.funding = undefined;
    } else if (
      normalizedResearchOrganizationData.funding &&
      normalizedResearchOrganizationData.funding.type !== ResearchOrganizationFundingType.FOREIGN_NATION
    ) {
      normalizedResearchOrganizationData.funding = {
        ...normalizedResearchOrganizationData.funding,
        countries: undefined,
      };
    }
  }

  const payload = {
    ...companyData,
    country: normalizedCountry,
    state: normalizedState,
    researchOrganizationData: normalizedResearchOrganizationData,
  };

  const cleaned: CompanyCreateDto = removeEmptyStrings(payload, true);

  return { data: cleaned, success: true };
};

const getCompanyFormDefaults = (params: {
  company?: CompanySelectDto;
  isEdit: boolean;
}): CompanyFormData => {
  const { company, isEdit } = params;

  return company && isEdit ? mapCompanyToFormValues(createBaseDefaults(), company) : createBaseDefaults();
};

export const CompanyProfileModal: FunctionComponent<CompanyProfileProps> = (props) => {
  const { company, isEdit = false, isOpen, mode = 'innovator', onClose, onCompanyCreated, onContinue } = props;
  const isAdminIntel = mode === 'admin-intel';

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);
  const wasModalOpenRef = useRef(false);

  const { mutateAsync: createCompanyProfile, isPending: createPending } = useCreateCompanyProfile();
  const { mutateAsync: createAdminOrganization, isPending: createAdminOrgPending } = useAdminCreateOrganization();
  const { mutateAsync: updateCompanyProfile, isPending: updatePending } = useUpdateCompany();

  const defaultValues = useMemo(
    (): CompanyFormData =>
      getCompanyFormDefaults({
        company,
        isEdit,
      }),
    [company, isEdit],
  );
  const {
    handleInviteAdd,
    handleInviteChangeRole,
    handleInviteRemove,
    handleUserChangeRole,
    handleUserRemove,
    invites,
    isInvitesPending,
    resetInvites,
    submitCompanyInvites,
  } = useCompanyProfileModalInvites();
  const {
    applyCompanyLogoDraft,
    companyLogoError,
    companyLogoPreviewUrl,
    handleCompanyLogoFileSelect,
    handleCompanyLogoRemove,
    handleCompanyLogoRestore,
    isCompanyLogoDeleteRequested,
    isCompanyLogoMutationPending,
    resetCompanyLogoDraft,
  } = useCompanyProfileModalLogo({
    existingCompanyLogoUrl: company?.logoUrl,
  });
  const { submitCompanyWithLogo } = useCompanyProfileModalSubmit({
    applyCompanyLogoDraft,
    createCompanyProfile,
    prepareSubmission,
    updateCompanyProfile,
  });

  const form = useAppForm({
    ...companyFormOptions,
    defaultValues,
    validators: {
      onSubmit: ({ value }) => {
        const schema = getStepSchema(step, value.type, mode);

        if (!schema) {
          return;
        }

        const validationResult = schema.safeParse(value);

        if (validationResult.success) {
          return;
        }

        return createStepValidationError(validationResult.error.issues);
      },
    },
    canSubmitWhenInvalid: true,
    onSubmitInvalid: createFormInvalidHandler<CompanyFormData>(),
    onSubmit: async ({ value }) => {
      const formData = value;
      const stepOrder = stepData.map((item) => item.step);
      const currentIndex = stepOrder.indexOf(step);
      const nextStep = stepOrder[currentIndex + 1];

      if (isEdit) {
        const updatedCompany = await submitCompanyWithLogo({
          formData,
          mode: 'edit',
        });
        if (updatedCompany) {
          onCloseOverride();
        }
        return;
      }

      if (step === Steps.TEAM) {
        if (isAdminIntel) {
          // Members are added live in the Team step; finishing just closes the wizard.
          onCloseOverride();
          return;
        }
        if (!companyId) {
          errorToast({ description: 'Organization ID is missing.' });
          return;
        }
        const invitesSubmitted = await submitCompanyInvites(companyId);
        if (invitesSubmitted) {
          setIsSuccessModalOpen(true);
        }
        return;
      }

      // Admin-intel has no Profile step, so the org is created atomically when leaving Details.
      if (isAdminIntel && step === Steps.DETAILS) {
        const payload = buildAdminIntelPayload(formData);
        if (!payload) {
          errorToast({ description: 'Please complete the required fields.' });
          return;
        }
        const createdOrganization = await createAdminOrganization(payload);
        setCompanyId(createdOrganization.id);
        onCompanyCreated?.(createdOrganization.id);
        updateStepState(step);
        handleStepClickBase(Steps.TEAM);
        return;
      }

      if (step === Steps.PROFILE) {
        const createdCompany = await submitCompanyWithLogo({
          formData,
          mode: 'create',
        });
        if (!createdCompany) {
          return;
        }

        setCompanyId(createdCompany.id);
        onCompanyCreated?.(createdCompany.id);
        updateStepState(step);
        handleStepClickBase(Steps.TEAM);
        return;
      }

      updateStepState(step);
      if (nextStep !== undefined) {
        setStep(nextStep);
      }
    },
  });

  const companyType = useStore(form.store, (state) => state.values.type);
  const formValues = useStore(form.store, (state) => state.values as CompanyFormData);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const detailsPresentation = getCompanyProfileDetailsPresentation({
    companyType: company?.type ?? companyType,
    isEdit,
    mode,
  });

  const stepData: StepConfig[] = useMemo(
    () => buildCompanyProfileStepData({ companyType, isEdit, mode }),
    [companyType, isEdit, mode],
  );
  const initialStep = stepData[0]?.step ?? Steps.TYPE;

  const {
    step,
    setStep,
    stepsInfo,
    setStepsInfo,
    updateStepState,
    handleStepClick: handleStepClickBase,
    resetSteps,
  } = useSteps(stepData, initialStep, isEdit);

  const resetModalState = useCallback(
    (params?: { withStepReset?: boolean }) => {
      const { withStepReset = true } = params ?? {};

      if (withStepReset) {
        resetSteps();
      }

      form.reset(defaultValues);
      resetCompanyLogoDraft();
      resetInvites();
      setCompanyId(undefined);
      setIsSuccessModalOpen(false);
    },
    [defaultValues, form, resetCompanyLogoDraft, resetInvites, resetSteps],
  );

  const ensureCurrentStepIsValid = useCallback((): boolean => {
    const schema = getStepSchema(step, companyType, mode);

    if (!schema) {
      return true;
    }

    const validationResult = schema.safeParse(formValues);
    if (validationResult.success) {
      return true;
    }

    errorToast({ description: 'Please fill in all required fields correctly.' });
    return false;
  }, [companyType, formValues, mode, step]);

  useEffect(() => {
    if (isEdit) {
      setStepsInfo((previous) =>
        previous.map((info) => ({
          ...info,
          state: info.step === step ? StepState.ACTIVE : StepState.NAVIGABLE,
        })),
      );
    }
  }, [isEdit, setStepsInfo, step]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const isWithinSelector = (event.target as HTMLElement)?.closest('[role="combobox"]');

    if (event.key === 'Enter' && !(event.target instanceof HTMLTextAreaElement || isWithinSelector)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      wasModalOpenRef.current = false;
      return;
    }

    window.addEventListener('keydown', handleKeyDown);

    if (!wasModalOpenRef.current) {
      resetModalState({ withStepReset: false });
      wasModalOpenRef.current = true;
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, isOpen, resetModalState]);

  useEffect(() => {
    if (companyType !== CompanyType.RESEARCH_PROJECT) return;
    form.setFieldValue('urls.linkedin', '');
    form.setFieldValue('urls.twitter', '');
    form.setFieldValue('urls.crunchbase', '');
    form.setFieldMeta('urls.linkedin', resetFieldMeta);
    form.setFieldMeta('urls.twitter', resetFieldMeta);
    form.setFieldMeta('urls.crunchbase', resetFieldMeta);
  }, [companyType, form]);

  const onCloseOverride = () => {
    resetModalState();
    onClose();
  };

  const handleStepClick = (targetStep: Steps) => {
    if (!ensureCurrentStepIsValid()) {
      return;
    }

    handleStepClickBase(targetStep);
  };

  const handleBack = () => {
    if (!ensureCurrentStepIsValid()) {
      return;
    }

    const stepOrder = stepData.map((item) => item.step);
    const currentIndex = stepOrder.indexOf(step);
    const previousStep = stepOrder[currentIndex - 1];

    if (previousStep !== undefined) {
      handleStepClickBase(previousStep);
    }
  };
  const currentStepData = stepData.find((item) => item.step === step);

  if (isSuccessModalOpen) {
    const city = formValues.city || '';
    const countryCode = formValues.country || '';
    const country = countries.find((country) => country.code === countryCode)?.code || countryCode;
    const stateCode = formValues.state || '';
    const state =
      usStates.find((stateItem) => stateItem.name === stateCode || stateItem.code === stateCode)?.code || stateCode;

    return (
      <SuccessStep
        city={city}
        companyName={formValues.name}
        country={country}
        state={state}
        onContinue={onContinue ?? (() => {})}
      />
    );
  }

  return (
    <Modal
      footer={{
        leftButton:
          // Once the admin org is created (Team step), there is no going Back to re-create it.
          !isEdit && step !== Steps.TYPE && !(isAdminIntel && step === Steps.TEAM)
            ? {
                onClick: handleBack,
                children: 'Back',
                variant: 'secondary' as const,
              }
            : undefined,
        rightButton: {
          loading:
            createAdminOrgPending ||
            createPending ||
            isCompanyLogoMutationPending ||
            isInvitesPending ||
            isSubmitting ||
            updatePending,
          onClick: () => {
            form.setErrorMap({});
            form.resetFieldMeta({});
            form.handleSubmit();
          },
          children: isEdit
            ? 'Update'
            : step === Steps.TEAM
              ? isAdminIntel
                ? 'Done'
                : 'Send Invites'
              : (isAdminIntel && step === Steps.DETAILS) || step === Steps.PROFILE
                ? 'Create'
                : 'Continue',
        },
      }}
      header={{
        title: `${isEdit ? 'Update' : 'Add'} Organization`,
      }}
      isOpen={isOpen}
      onClose={onCloseOverride}
      width="760px"
    >
      <div className="pb-6">
        <CompanyEditNavBar
          handleStepClick={handleStepClick}
          isEdit={isEdit}
          stepsInfo={stepsInfo}
        />
      </div>

      {currentStepData?.guidance && (
        <div className="text-md font-normal mx-2 text-[#1F1C1AB2] flex whitespace-pre-wrap my-[-10px] mb-[30px]">
          {currentStepData.guidance}
        </div>
      )}

      <div className="flex flex-col gap-6 px-4 pb-6">
        {step === Steps.TYPE && (
          <TypeStep
            adminIntel={isAdminIntel}
            form={form}
          />
        )}
        {step === Steps.DETAILS &&
          (isAdminIntel ? (
            <DetailsIntelSection form={form} />
          ) : (
            <DetailsStep
              companyLogoError={companyLogoError}
              companyLogoPreviewUrl={companyLogoPreviewUrl}
              form={form}
              isCompanyLogoDeleteRequested={isCompanyLogoDeleteRequested}
              onCompanyLogoFileSelect={handleCompanyLogoFileSelect}
              onCompanyLogoRemove={handleCompanyLogoRemove}
              onCompanyLogoRestore={handleCompanyLogoRestore}
              showCompanyLogo={detailsPresentation.showCompanyLogo}
            />
          ))}
        {!isAdminIntel && companyType === CompanyType.COMMERCIAL && step === Steps.FINANCIAL && (
          <FinancialStep form={form} />
        )}
        {step === Steps.PROFILE && <ProfileStep form={form} />}
        {step === Steps.TEAM &&
          (isAdminIntel ? (
            <AdminIntelTeamStep companyId={companyId ?? ''} />
          ) : (
            <TeamStepComponent
              companyId={companyId ?? ''}
              handleInviteAdd={handleInviteAdd}
              handleInviteChangeRole={handleInviteChangeRole}
              handleInviteRemove={handleInviteRemove}
              handleUserChangeRole={handleUserChangeRole}
              handleUserRemove={handleUserRemove}
              invites={invites}
              users={[]}
            />
          ))}
      </div>
    </Modal>
  );
};
